import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { ACCEPT_VIDEO_MIMETYPE, UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import { useGlobalModal, useVideoUpload } from '@/app/hooks';
import type { AgentMediaItem, VideoUploadFile, VideoUploadFileInfo } from '@/app/types';
import type { UploadedImage, UploadedVideo } from '../_components/Chat/types';
import { MAX_VIDEO_DURATION_IN_SECONDS, MAX_VIDEO_FILE_SIZE, MINI_VIDEO_DURATION_IN_SECONDS } from '../constants';
import { useImageUpload } from './use-image-upload';

interface UseUploadManagerProps {
  // 上传配置
  imageLimit: number;
  videoLimit: number;
  scene?: UploadScene; // 上传场景，agent 用 'agent'，默认为 'vidu'
  /** 支持的视频格式 */
  videoAccept?: string[];

  // Store 状态和 actions
  uploadList: AgentMediaItem[];
  addUploadFile: (file: AgentMediaItem) => void;
  updateUploadFile: (fileId: string, updates: Partial<AgentMediaItem>) => void;
  removeUploadFile: (fileId: string) => void;

  // 回调函数（可选）
  onImageUpload?: (images: UploadedImage[]) => void;
  onVideoUpload?: (videos: UploadedVideo) => void;
  onUploadError?: (error: string) => void;
}

/**
 * 上传管理 Hook（与 Store 集成版本）
 * 处理文件上传的状态管理、队列管理和生命周期
 * 不再维护内部状态，直接使用 Store 的状态和 actions
 */
export function useUploadManager({
  imageLimit,
  videoLimit,
  scene = UPLOAD_SCENE.VIDU,
  videoAccept = ACCEPT_VIDEO_MIMETYPE,
  uploadList,
  addUploadFile,
  updateUploadFile,
  removeUploadFile,
  onImageUpload,
  onVideoUpload,
  onUploadError,
}: UseUploadManagerProps) {
  const t = useTranslations();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');

  // 添加上传项（开始上传时调用）
  const addUploadItem = useCallback(
    (type: 'image' | 'video', ids: string[]) => {
      const newItems: AgentMediaItem[] = ids.map((id) => {
        const item = {
          tempId: id,
          id: '',
          type,
          status: 'uploading' as const,
          url: '',
        };
        addUploadFile(item);
        return item;
      });
      return newItems;
    },
    [addUploadFile],
  );

  /** 最新的上传的文件数量限制 */
  const lastUploadLimit = useMemo(() => {
    const imgCount = uploadList.filter((item) => item.type === 'image').length;
    const videoCount = uploadList.filter((item) => item.type === 'video').length;

    return {
      image: imageLimit - imgCount,
      video: videoLimit - videoCount,
    };
  }, [uploadList.length, imageLimit, videoLimit]);

  // 检查是否可以上传指定类型
  const canUpload = useCallback(
    (type: 'image' | 'video') => {
      return lastUploadLimit[type] > 0;
    },
    [lastUploadLimit],
  );

  // 图片上传 Hook
  const { selectImages, handleFilesChange } = useImageUpload({
    maxCount: lastUploadLimit.image,
    scene,
    onUploadStart: (ids) => {
      addUploadItem('image', ids);
    },
    onUploadSuccess: (images) => {
      // 为每张图片更新上传项
      const newItems: AgentMediaItem[] = images.map((image) => ({
        id: image.uploadId ?? '',
        tempId: image.id ?? '',
        type: 'image',
        status: 'done',
        url: image.selectedImagePreview,
        cover_url: image.selectedImagePreview,
      }));

      newItems.forEach((item) => {
        updateUploadFile(item?.tempId ?? '', item);
      });
      onImageUpload?.(images);
    },
    onUploadError: (error, id) => {
      if (id) {
        removeUploadFile(id);
      }

      if (error.failReason === 'AuditSubmitIllegal') {
        const auditId = error.metadata?.audit_id;
        openConfirmDialog({
          title: t('popup.dialog.error.checkFailed.title'),
          content: auditId
            ? `${t('popup.dialog.error.checkFailed.content')}\n(ID: ${auditId})`
            : t('popup.dialog.error.checkFailed.content'),
          primaryButtonText: t('popup.dialog.error.confirm'),
        });
      } else {
        // 显示错误提示
        toast({
          type: 'error',
          message: error.failReason || t('popup.toast.uploadingError'),
        });
      }

      onUploadError?.(error.failReason);
    },
  });

  // 视频上传状态变化处理
  const handleVideoUploadChange = useCallback(
    (file: VideoUploadFile, fileUploadInfo?: VideoUploadFileInfo) => {
      const { id, file: video, status } = file;

      if (status === 'uploading') {
        // 开始上传：添加上传项
        addUploadItem('video', [id]);
        return;
      }

      if (status === 'done' && video) {
        // 上传成功：更新上传项
        updateUploadFile(id, {
          id: video.id ?? '',
          type: 'video',
          status: 'done',
          url: video.preview,
          cover_url: video.cover_url || video.cover_preview_url,
        });

        // 转换为 UploadedVideo 格式并调用回调
        const uploadedVideo: UploadedVideo = {
          src: video.src,
          preview: video.preview,
          aspectRatio: video.aspectRatio,
          duration: video.duration,
        };
        onVideoUpload?.(uploadedVideo);
        return;
      }

      if (status === 'error') {
        // 上传失败：移除上传项并显示错误提示
        removeUploadFile(id);

        onUploadError?.(fileUploadInfo?.fail_reason || t('popup.toast.uploadingError'));
        return;
      }

      if (status === 'removed') {
        // 取消上传：移除上传项
        removeUploadFile(id);
      }
    },
    [addUploadItem, updateUploadFile, removeUploadFile, onVideoUpload, onUploadError, t],
  );

  // 视频上传 Hook
  const { selectVideo } = useVideoUpload({
    minDuration: MINI_VIDEO_DURATION_IN_SECONDS,
    maxDuration: MAX_VIDEO_DURATION_IN_SECONDS,
    maxSize: MAX_VIDEO_FILE_SIZE,
    scene,
    videoAccept,
    captureCoverImage: true,
    onChange: handleVideoUploadChange,
  });

  return {
    canUpload,
    selectImages,
    selectVideo,
    handleImagesChange: handleFilesChange,
  };
}
